import React, { useState, useEffect } from "react";
import {
  Flex,
  FormControl,
  Text,
  useToast,
  Switch,
  FormLabel,
  Input,
  Box,
} from "@chakra-ui/react";
import { useDispatch } from "react-redux";
import {
  white,
  newBorder,
  textPrimary,
  textSecondary,
  backgroundLight,
} from "../../../../../utils/colors";
import { useTranslation } from "react-i18next";
import FormControlCountry from "../../../../../components/input/FormControlCountry";
import FormControlDefault from "../../../../../components/input/FormControlDefault";
import useThemeColors from "../../../../../hooks/useThemeColors";
import { useSelector } from "react-redux";
import {
  FaCcVisa,
  FaCcMastercard,
  FaCcAmex,
  FaCcDiscover,
  FaCcDinersClub,
  FaCcJcb,
} from "react-icons/fa";

const BillingDetailItem = ({ label, value, textColor }) => {
  return (
    <Flex align="baseline">
      <Text fontSize="sm" fontWeight="500" color="gray.500" mr={1}>
        {label}:
      </Text>
      <Text fontSize="sm" fontWeight="500" color={textColor} m="0px 0 0">
        {value || "-"}
      </Text>
    </Flex>
  );
};

const getCardIcon = (brand) => {
  switch (brand.toLowerCase()) {
    case "visa":
      return <FaCcVisa size={28} color="#1A1F71" />;
    case "mastercard":
      return <FaCcMastercard size={28} color="#F79E1B" />;
    case "amex":
    case "american express":
      return <FaCcAmex size={28} color="#2E77BC" />;
    case "discover":
      return <FaCcDiscover size={28} color="#FF6000" />;
    case "diners":
    case "diners club":
      return <FaCcDinersClub size={28} color="#0069AA" />;
    case "jcb":
      return <FaCcJcb size={28} color="#004B97" />;
    // case "unionpay":
    //   return <SiUnionpay size={32} color="#D51222" />;
    default:
      return null; // No icon found
  }
};

const SubscriptionBillingDetails = ({
  tenant,
  subscription,
  priceId,
  address,
  setAddress,
  companyName,
  setCompanyName,
  companyCode,
  setCompanyCode,
  isIndividual,
  setIsIndividual,
  fullName,
  setFullName,
  vatCode,
  setVatCode,
  checkVatCodeHandler,
  loadingCheckUpdateVatCode,
  vatCodeStatus,
  removeTaxIdHandler,
}) => {
  const { t } = useTranslation();

  const toast = useToast();

  const { textPrimaryMode } = useThemeColors();

  // STATE

  // REDUX
  const dispatch = useDispatch();

  // HANDLERS

  // VALIDATION

  // USE EFFECT

  // LOGS
  console.log("tenant:", tenant);
  console.log("subscription:", subscription);

  return (
    <>
      <Flex
        w="100%"
        h="100%"
        direction="column"
        overflowY="auto"
        p="1.5rem 0rem 0rem"
      >
        {/* BILLING DETAILS START */}
        <Flex direction="column" w="100%">
          <Flex align="center" justify="space-between" w="100%">
            <Text
              fontSize="0.875rem"
              fontWeight="600"
              color={textPrimaryMode}
              m="0 0 0.5rem"
            >
              {t("module.billingDetails")}
            </Text>
          </Flex>

          <Flex
            direction="column"
            w="100%"
            bg={backgroundLight}
            borderRadius="1rem"
            p="1.25rem 1.5rem 1.25rem"
          >
            <Flex
              w="100%"
              bg={white}
              p="1.5rem 1.5rem"
              borderRadius="1rem"
              direction="column"
            >
              <Flex
                w="100%"
                align="flex-start"
                justify="space-between"
                gap="2rem"
              >
                <Box>
                  <Text
                    fontSize="0.875rem"
                    fontWeight="600"
                    color={textPrimaryMode}
                    m="0 0 0.5rem"
                  >
                    {t("module.billingInformation")}
                  </Text>
                  {subscription.isIndividual ? (
                    <>
                      <Flex direction="column" gap="0.5rem">
                        <BillingDetailItem
                          label={t("form.fullName")}
                          value={subscription.fullName}
                          textColor={textPrimary}
                        />
                        <BillingDetailItem
                          label={t("form.address")}
                          value={`${subscription.billingAddress.street}, ${subscription.billingAddress.zip}, ${subscription.billingAddress.city}, ${subscription.billingAddress.country}`}
                          textColor={textPrimary}
                        />
                      </Flex>
                    </>
                  ) : (
                    <>
                      <Flex direction="column" gap="0.5rem">
                        <BillingDetailItem
                          label={t("form.companyName")}
                          value={subscription.companyName}
                          textColor={textPrimary}
                        />
                        <BillingDetailItem
                          label={t("form.companyCode")}
                          value={subscription.companyCode}
                          textColor={textPrimary}
                        />
                        <BillingDetailItem
                          label={t("form.vatCode")}
                          value={subscription.vatCode}
                          textColor={textPrimary}
                        />
                        <BillingDetailItem
                          label={t("form.address")}
                          value={`${subscription.billingAddress.street}, ${subscription.billingAddress.zip}, ${subscription.billingAddress.city}, ${subscription.billingAddress.country}`}
                          textColor={textPrimary}
                        />
                      </Flex>
                    </>
                  )}
                </Box>

                {/* CARD DETAILS */}
                <Box>
                  <Text
                    fontSize="0.875rem"
                    fontWeight="600"
                    color={textPrimaryMode}
                    m="0 0 0.5rem"
                  >
                    {t("module.paymentMethod")}
                  </Text>
                  <Flex
                    bg={backgroundLight}
                    p="1rem"
                    borderRadius="1rem"
                    direction="column"
                    align="flex-start"
                    minW="240px"
                  >
                    {/* CARD ICON */}
                    <Flex justify="center" mb="0.5rem">
                      {/* {getCardIcon(subscription.paymentMethod.brand)} */}
                    </Flex>
                    {/* CARD NUMBER */}
                    <Flex align="center">
                      <Text
                        fontSize="0.875rem"
                        fontWeight="600"
                        color={textSecondary}
                        m="0 0 0.25rem"
                      >
                        {/* **** **** **** {subscription.paymentMethod.last4} */}
                      </Text>
                    </Flex>

                    {/* EXPIRES ON */}
                    <Flex align="center">
                      <Text
                        fontSize="0.8rem"
                        fontWeight="500"
                        color={textSecondary}
                        // m="0 0 0.5rem"
                      >
                        {t("module.expiresOn")}{" "}
                        {/* {subscription.paymentMethod.exp_month}/ */}
                        {/* {subscription.paymentMethod.exp_year} */}
                      </Text>
                    </Flex>
                  </Flex>
                </Box>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </>
  );
};

export default SubscriptionBillingDetails;
